import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GRAPH_STATUSES } from '@Constants/constants';
import { BreakdownMetric } from './breakdownSelection';
import './BreakdownProgressBar.scss';

export interface BreakdownGraphPoint {
    id: string;
    value: number;
}

interface Segment {
    id: string;
    color: string;
    value: number;
    percentage: number;
    /** Null for a status the endpoint sent that GRAPH_STATUSES does not know — see below. */
    metric: BreakdownMetric | null;
}

// Colour and order come from GRAPH_STATUSES so the bar cannot drift from the location row's;
// the metric is this file's own mapping from a segment onto the drill-down that lists it.
const STATUS_ORDER: { label: string; color: string; metric: BreakdownMetric }[] = [
    { status: GRAPH_STATUSES.NotApplicable, metric: 'notApplicable' as const },
    { status: GRAPH_STATUSES.Complete, metric: 'complete' as const },
    { status: GRAPH_STATUSES.PastDue, metric: 'pastDue' as const },
    { status: GRAPH_STATUSES.InProgress, metric: 'inProgress' as const },
    { status: GRAPH_STATUSES.NotDue, metric: 'notDue' as const },
].map(({ status, metric }) => ({ label: status.label, color: status.color, metric }));

const UNKNOWN_STATUS_COLOR = '#6c757d';

const formatPercentage = (percentage: number): string =>
    Number.isFinite(percentage) ? percentage.toFixed(1).replace(/\.0$/, '') : '0';

const buildSegments = (points: BreakdownGraphPoint[]): { segments: Segment[]; total: number } => {
    const valid = Array.isArray(points) ? points.filter((point) => Number.isFinite(point?.value)) : [];
    const total = valid.reduce((sum, point) => sum + point.value, 0);

    const sumFor = (id: string) =>
        valid.filter((point) => point.id === id).reduce((sum, point) => sum + point.value, 0);

    const known = STATUS_ORDER.map((status) => ({
        id: status.label,
        color: status.color,
        value: sumFor(status.label),
        metric: status.metric,
    }));

    // A status the endpoint reports that this build does not know about. It still gets a
    // segment, but no drill-down: there is no metric to ask the endpoint for, and a control
    // that opens an empty list is worse than a plain block of colour.
    const unknownIds = Array.from(
        new Set(valid.map((point) => point.id).filter((id) => !STATUS_ORDER.some((status) => status.label === id)))
    );
    const unknown = unknownIds.map((id) => ({ id, color: UNKNOWN_STATUS_COLOR, value: sumFor(id), metric: null }));

    return {
        total,
        segments: [...known, ...unknown]
            .filter((segment) => segment.value > 0)
            .map((segment) => ({ ...segment, percentage: total > 0 ? (segment.value / total) * 100 : 0 })),
    };
};

interface TooltipPosition {
    x: number;
    y: number;
}

// The tooltip's own footprint, give or take — enough to decide whether it should flip to the
// other side of the cursor rather than run off the viewport. The height is derived from the row
// count rather than fixed: a two-status row's card is half the height of a five-status one, and a
// single worst-case constant would flip it above the cursor in the bottom third of the screen for
// no reason. Values follow BreakdownProgressBar.scss — title 17px, list rows 19px, the total line
// 26px, and 12px of padding.
const TOOLTIP_WIDTH = 190;
const CURSOR_OFFSET = 14;
const tooltipHeight = (rowCount: number): number => 55 + rowCount * 19;

// Sits below-right of the cursor, and flips to the other side on whichever axis would run it
// off screen — the Progress column is the widest one, so a right-edge row is the normal case,
// not the exception.
const tooltipStyle = ({ x, y }: TooltipPosition, rowCount: number): React.CSSProperties => {
    const height = tooltipHeight(rowCount);
    const fitsRight = window.innerWidth - x > TOOLTIP_WIDTH + CURSOR_OFFSET;
    const fitsBelow = window.innerHeight - y > height + CURSOR_OFFSET;

    return {
        left: fitsRight ? x + CURSOR_OFFSET : Math.max(0, x - TOOLTIP_WIDTH - CURSOR_OFFSET),
        top: fitsBelow ? y + CURSOR_OFFSET : Math.max(0, y - height - CURSOR_OFFSET),
    };
};

interface BreakdownProgressBarProps {
    taskGraph: BreakdownGraphPoint[];
    /** Names the row this bar belongs to, for the screen-reader label. */
    groupName: string;
    /**
     * Drills into one status's tasks. Omitted, the bar stays a picture: it keeps `role='img'`
     * and its single tab stop, because a group of buttons that do nothing is a worse keyboard
     * experience than one described image.
     */
    onSegmentSelect?: (metric: BreakdownMetric) => void;
}

/**
 * The Progress column's stacked bar: one row's tasks split across the five completion
 * statuses, sized by share and hover-explained by exact counts.
 *
 * Deliberately not the location row's `NivoTaskCompletionBar`: that is an SVG chart with its
 * own resize observer per instance, and the breakdown renders up to forty of them in a
 * scrolling table. The visual contract — status order, colours, tooltip wording — is shared;
 * the machinery is not.
 */
const BreakdownProgressBar = ({ taskGraph, groupName, onSegmentSelect }: BreakdownProgressBarProps): ReactElement => {
    const barRef = useRef<HTMLDivElement>(null);
    const [tooltipAt, setTooltipAt] = useState<TooltipPosition | null>(null);
    const [hoveredId, setHoveredId] = useState<string | null>(null);

    const { segments, total } = useMemo(() => buildSegments(taskGraph), [taskGraph]);

    // The whole bar is one hover target, so the tooltip can report every status at once —
    // comparing "past due" against "complete" is the point of the column, and that comparison
    // is lost if the user has to hover each segment in turn to see its numbers. The segment
    // actually under the cursor is highlighted inside it, so the bar still answers "what is
    // this piece?" the way the location row's per-segment tooltip does.
    //
    // Bound to enter as well as move: a bar that arrives under a stationary cursor — the table
    // re-sorting, the panel opening beneath the pointer — gets no mousemove, and would sit
    // hovered and silent until the user jiggled the mouse.
    const onMouseMove = useCallback((event: React.MouseEvent) => {
        setTooltipAt({ x: event.clientX, y: event.clientY });
    }, []);

    const onFocus = useCallback(() => {
        const rect = barRef.current?.getBoundingClientRect();
        if (rect) setTooltipAt({ x: rect.left + rect.width / 2, y: rect.top });
    }, []);

    const hideTooltip = useCallback(() => {
        setTooltipAt(null);
        setHoveredId(null);
    }, []);

    // Keyed on whether the tooltip is open, not on where it is: `tooltipAt` is a fresh object on
    // every mousemove, so depending on it would tear the listeners down and rebuild them for each
    // pixel the cursor travels.
    const isTooltipOpen = tooltipAt !== null;

    useEffect(() => {
        if (!isTooltipOpen) return undefined;

        // The tooltip is positioned in viewport coordinates, but the bar it describes lives in a
        // scrolling table, and scrolling fires no mousemove — without this the card would hang at
        // the coordinates of a row that has since slid out from under it. Captured, because the
        // scroll that moves the bar is the table's own, not the window's, and scroll does not bubble.
        window.addEventListener('scroll', hideTooltip, true);

        // Hover and focus content has to be dismissible without moving the pointer or losing focus
        // (WCAG 1.4.13). Bound to the document rather than to the bar so it works while merely
        // hovering, when the bar holds no focus and would see no key event of its own.
        const onEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') hideTooltip();
        };
        document.addEventListener('keydown', onEscape);

        return () => {
            window.removeEventListener('scroll', hideTooltip, true);
            document.removeEventListener('keydown', onEscape);
        };
    }, [isTooltipOpen, hideTooltip]);

    const summary = useMemo(
        () =>
            segments
                .map((segment) => `${segment.id} ${segment.value} (${formatPercentage(segment.percentage)}%)`)
                .join(', '),
        [segments]
    );

    if (total <= 0) {
        // No tasks in this group at all. An empty track would read as "0% complete", and an em dash
        // here would double the one the cell's percentage already shows — the cell is left to say
        // "nothing to report" once, the way the count columns do.
        return <></>;
    }

    // Rendered into the body, not the cell: the table scrolls inside the breakdown panel, and
    // anything absolutely positioned in a cell is clipped at that scroll box's edge — which is
    // exactly where the first and last rows' tooltips would land.
    const tooltip = tooltipAt
        ? createPortal(
            <div
                className='bpb-tooltip'
                role='tooltip'
                style={tooltipStyle(tooltipAt, segments.length)}>
                <div className='bpb-tooltip__title'>{groupName}</div>
                <ul className='bpb-tooltip__list'>
                    {segments.map((segment) => (
                        <li
                            key={segment.id}
                            className={`bpb-tooltip__row${segment.id === hoveredId ? ' bpb-tooltip__row--active' : ''}`}>
                            <span className='bpb-tooltip__swatch' style={{ backgroundColor: segment.color }} />
                            <span className='bpb-tooltip__label'>{segment.id}</span>
                            <span className='bpb-tooltip__value'>
                                {segment.value} ({formatPercentage(segment.percentage)}%)
                            </span>
                        </li>
                    ))}
                </ul>
                <div className='bpb-tooltip__total'>{total} tasks</div>
            </div>,
            document.body
        )
        : null;

    // With a drill-down the segments are the controls and each is its own tab stop, so the bar
    // becomes a labelled group: `role='img'` makes everything inside it presentational, which
    // would hide those buttons from a screen reader entirely.
    const isInteractive = onSegmentSelect !== undefined;

    return (
        <>
            <div
                ref={barRef}
                className='bpb'
                role={isInteractive ? 'group' : 'img'}
                aria-label={`${groupName}: ${summary}`}
                tabIndex={isInteractive ? undefined : 0}
                onMouseEnter={onMouseMove}
                onMouseMove={onMouseMove}
                onMouseLeave={hideTooltip}
                // Focus and blur bubble in React, so a segment button taking focus opens the
                // tooltip through the same handlers the whole-bar tab stop uses.
                onFocus={onFocus}
                onBlur={hideTooltip}>
                {segments.map((segment) => {
                    const style = { width: `${segment.percentage}%`, backgroundColor: segment.color };
                    // An unknown status has no metric to drill into; it renders as the plain
                    // block it did before.
                    if (!onSegmentSelect || segment.metric === null) {
                        return (
                            <span
                                key={segment.id}
                                className='bpb__segment'
                                style={style}
                                onMouseEnter={() => setHoveredId(segment.id)}
                            />
                        );
                    }

                    const metric = segment.metric;
                    return (
                        <button
                            key={segment.id}
                            type='button'
                            className='bpb__segment bpb__segment--clickable'
                            style={style}
                            aria-label={`Show ${segment.id} tasks for ${groupName}: ${segment.value} (${formatPercentage(segment.percentage)}%)`}
                            onMouseEnter={() => setHoveredId(segment.id)}
                            onFocus={() => setHoveredId(segment.id)}
                            onClick={() => onSegmentSelect(metric)}
                        />
                    );
                })}
            </div>
            {tooltip}
        </>
    );
};

export default BreakdownProgressBar;
